import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { generateAccessiblePatterns } from 'src/app/shared/chart-patterns/chart-patterns';

interface ProgramStatusDistributionItem {
  status: string;
  count: number;
}

interface VerifiedCoverageByClassificationItem {
  classification: string;
  totalEligibleAssets: number;
}

/** Dashboard payload with dashboard-specific fields. */
export interface DataProtectionDashboardData {
  dataAssets?: number | null;
  mappedAssets?: number | null;
  verifiedProtection?: number | null;
  needsAttention?: number | null;
  programStatusDistribution?: ProgramStatusDistributionItem[] | null;
  verifiedCoverageByClassification?: VerifiedCoverageByClassificationItem[] | null;
}

interface DashboardRouteData {
  dashBoardData?: DataProtectionDashboardData | null;
  chartData?: DataProtectionDashboardData | null;
}

@Component({
  selector: 'app-auth-tool-home',
  templateUrl: './data-protection-dashboard.component.html',
  styleUrls: ['./data-protection-dashboard.component.scss'],
})
export class DataProtectionDashboardComponent implements OnInit {
  constructor(private readonly route: ActivatedRoute) {}

  dashBoardData: DataProtectionDashboardData | null = null;
  programStatusDistribution: ProgramStatusDistributionItem[] = [];
  verifiedCoverageByClassification: VerifiedCoverageByClassificationItem[] = [];
  programStatusChartData: any = { labels: [], datasets: [] };
  eligibleAssetsChartData: any = { labels: [], datasets: [] };

  readonly programStatusChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          color: '#4b5870',
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 16,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#07112e',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => `${context.label}: ${context.parsed ?? 0} program(s)`,
        },
      },
    },
  };

  eligibleAssetsChartOptions: any = {};

  ngOnInit(): void {
    const resolved = this.route.snapshot.data['dashboard'] as DashboardRouteData | undefined;
    this.dashBoardData = resolved?.dashBoardData ?? null;
    const chartResponse = resolved?.chartData ?? {};

    this.programStatusDistribution = Array.isArray(chartResponse.programStatusDistribution)
      ? chartResponse.programStatusDistribution
      : [];
    this.verifiedCoverageByClassification = Array.isArray(chartResponse.verifiedCoverageByClassification)
      ? chartResponse.verifiedCoverageByClassification
      : [];

    this.prepareProgramStatusChart();
    this.prepareEligibleAssetsChart();
  }

  private prepareProgramStatusChart(): void {
    // Keep each known status assigned to the same pattern, regardless of API order.
    const canonicalStatusOrder = [
      'PROGRAM_NOT_CREATED',
      'AWAITING_IMPLEMENTATION',
      'IMPLEMENTATION_IN_PROGRESS',
      'AWAITING_VERIFICATION',
      'PROGRAM_COMPLETED',
      'STATUS_REVIEW_REQUIRED',
    ];

    const normalizeStatus = (status: string | null | undefined): string =>
      String(status ?? '').trim().toUpperCase();

    // Append unknown statuses deterministically, so they also receive stable patterns.
    const unknownStatuses = Array.from(new Set(
      this.programStatusDistribution
        .map(item => normalizeStatus(item.status))
        .filter(status => status && !canonicalStatusOrder.includes(status)),
    )).sort();

    const patternOrder = [...canonicalStatusOrder, ...unknownStatuses];
    const patterns = generateAccessiblePatterns(patternOrder.length);
    const patternByStatus = new Map<string, CanvasPattern>(
      patternOrder.map((status, index) => [status, patterns[index]]),
    );

    this.programStatusChartData = {
      labels: this.programStatusDistribution.map(item => this.formatStatus(item.status)),
      datasets: [{
        label: 'Programs',
        data: this.programStatusDistribution.map(item => this.toNumber(item.count)),
        backgroundColor: this.programStatusDistribution.map(item =>
          patternByStatus.get(normalizeStatus(item.status)) ?? patterns[0],
        ),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 5,
      }],
    };
  }

  private prepareEligibleAssetsChart(): void {
    const patterns = generateAccessiblePatterns(this.verifiedCoverageByClassification.length);
    this.eligibleAssetsChartData = {
      labels: this.verifiedCoverageByClassification.map(item => item.classification || 'Unclassified'),
      datasets: [{
        label: 'Total Eligible Assets',
        data: this.verifiedCoverageByClassification.map(item => this.toNumber(item.totalEligibleAssets)),
        backgroundColor: patterns,
        borderColor: '#2850ad',
        borderWidth: 1.5,
        borderRadius: 6,
        maxBarThickness: 64,
      }],
    };

    this.eligibleAssetsChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          align: 'start',
          labels: {
            color: '#4b5870',
            usePointStyle: true,
            pointStyle: 'rectRounded',
            boxWidth: 9,
            boxHeight: 9,
            padding: 18,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: '#07112e',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          padding: 10,
          cornerRadius: 8,
          callbacks: {
            label: (context: any) => `Eligible assets: ${this.toNumber(context.parsed?.y)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: '#6f7b91', maxRotation: 0, autoSkip: true },
          title: {
            display: true,
            text: 'Data Classification',
            color: '#4b5870',
            font: { weight: '600' },
          },
        },
        y: {
          min: 0,
          grid: { color: '#e9edf3' },
          border: { display: false },
          ticks: {
            color: '#6f7b91',
            callback: (value: string | number) => this.toNumber(value).toLocaleString(),
          },
          title: {
            display: true,
            text: 'Total Eligible Assets',
            color: '#4b5870',
            font: { weight: '600' },
          },
        },
      },
    };
  }

  formatStatus(value: string | null | undefined): string {
    const text = String(value ?? '').trim();
    if (!text) return 'Unknown';
    return text.toLowerCase().split('_')
      .map(word => word ? word[0].toUpperCase() + word.slice(1) : word).join(' ');
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
